<script>
import Popup from "../../components/popup.vue";
import FinishedAnimation from "../../animations/Finished.vue";
import ThankYouAnimation from "../../animations/ThankYou.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import Icon from "../../components/Icon.vue";
import {useReportStore} from "../../../stores/report";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import {useOrganizationStore} from "../../../stores/organizations";

export default {
  name: 'HouseholdReportExporter',
  components: {Popup, FinishedAnimation, ThankYouAnimation, LoadingSpinner, Icon},

  props: {
    reportType: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: false
    },
    description: {
      type: String,
      required: false
    },
    showYears: {
      type: Boolean,
      default: false
    }
  },
  emits: ['generate-report'],

  setup() {
    const reportStore = useReportStore();
    const analyticEventStore = useAnalyticEventStore();
    const organizationStore = useOrganizationStore();

    return {
      reportStore,
      analyticEventStore,
      organizationStore
    };
  },
  data() {
    return {
      households: [],
      selectedHouseholds: [],
      selectedYear: null,
      searchQuery: '',
      loading: true,
      generating: false,
      finished: false,
      visibleYearCount: 5,
      uploadedReportsCount: 0,
      popupMessage: "Something went wrong generating the report!",
      paginatedHouseholdList: 50
    };
  },
  computed: {

    generateButtonEnabled() {
      if (this.selectedHouseholds.length === 0) return false;
      return !(this.showYears && !this.selectedYear);
    },

    filteredAvailableHouseholds() {
      if (!this.searchQuery.trim()) {
        return this.households;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.households.filter(household => {
        return household.name?.toLowerCase().includes(query);
      });
    },

    years() {
      const currentYear = new Date().getFullYear();
      const years = [];

      // Generate years from current year going backwards
      for (let i = 0; i < this.visibleYearCount; i++) {
        years.push(currentYear - i);
      }

      return years;
    },
    visibleHouseholds() {
      return this.filteredAvailableHouseholds.slice(0, this.paginatedHouseholdList);
    },
    hasMoreHouseholds() {
      return this.filteredAvailableHouseholds.length > this.paginatedHouseholdList;
    },
  },
  watch: {
    '$route.params.org'() {
      this.households = [];
      this.selectedHouseholds = [];
      this.loadHouseholds();
    },
    selectedHouseholds: {
      immediate: true,
      deep: true,
      handler() {
        this.countUploadedReports();
      }
    },
    searchQuery() {
      this.paginatedHouseholdList = 50;
    },
    selectedYear(){
      if (this.showYears) {
        this.loadHouseholds();
      }
    },
  },
  mounted() {
    if (this.showYears && !this.selectedYear) { // Prepopulate the selected year
      this.selectedYear = new Date().getFullYear();
    }
    this.loadHouseholds();
  },
  methods: {
    loadHouseholds() {
      this.loading = true;
      // For year-scoped household reports (e.g Household Giving Summary) limit the
      // list to households that gave in the selected year (WI-006, DONATE-1596).
      const givingYear = this.showYears ? this.selectedYear : null;
      this.reportStore.getHouseholds(givingYear)
        .then((households) => {
          this.households = households;
          // Drop any selected households no longer in the filtered list
          const availableIds = new Set(this.households.map(h => h.id));
          this.selectedHouseholds = this.selectedHouseholds.filter(h => availableIds.has(h.id));
          this.loading = false;
        });
    },

    addHousehold(household) {
      if (!this.selectedHouseholds.find(selected => selected.id === household.id)) {
        this.selectedHouseholds.push(household);
      }
    },

    toggleHousehold(household) {
      if (this.generating || this.finished) return;
      if (this.householdIsSelected(household)) {
        this.removeHousehold(household);
      } else {
        this.addHousehold(household);
      }
    },

    removeHousehold(household) {
      this.selectedHouseholds = this.selectedHouseholds.filter(selected => selected.id !== household.id);
    },

    addAllHouseholds() {
      if (this.filteredAvailableHouseholds.length > 100) {
        this.popupMessage = `Adding ${this.filteredAvailableHouseholds.length} households. Generating reports for this many households may take a few minutes.`;
        this.$refs.popup.show();
      }
      this.filteredAvailableHouseholds.forEach(household => {
        this.addHousehold(household);
      });
    },

    clearSelectedHouseholds() {
      this.selectedHouseholds = [];
    },

    clearSearch() {
      this.searchQuery = '';
    },

    reset() {
      this.selectedHouseholds = [];
      this.finished = false;
      this.generating = false;

      this.households.forEach(household => {
        household.pendingReportId = null;
        household.pendingReportStatus = null;
      });
    },

    generateReport() {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: `report-generated-${this.reportType}` });
      switch (this.reportType) {
        case 'household_giving_summary':
          this.exportHouseholdDonationSummary();
          break;
        case 'household_member_list':
          this.exportHouseholdMemberList();
          break;
      }
    },

    exportHouseholdDonationSummary() {
      this.generating = true;
      this.processInBatches(this.selectedHouseholds, (household) => {
        return this.reportStore.createHouseholdDonationSummary(household, this.selectedYear);
      }).then((reportIds) => {
        return this.reportStore.downloadMultipleReportsZipped(reportIds, this.selectedHouseholds);
      }).then(() => this.finished = true)
        .catch((e) => {
          this.popupMessage = e?.message || "Something went wrong generating the report!";
          this.$refs.popup.show();
          this.reset();
        });
    },

    // Processes items in parallel batches to avoid overwhelming the browser with too many concurrent requests.
    async processInBatches(items, fn, batchSize = 25) {
      const results = [];
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        const batchResults = await Promise.all(batch.map(fn));
        results.push(...batchResults);
      }
      return results;
    },

    exportHouseholdMemberList() {
      this.generating = true;

      // Create reports sequentially to avoid concurrent request issues
      const createSequentially = this.selectedHouseholds.reduce((chain, household) => {
        return chain.then((reportIds) => {
          return this.reportStore.createHouseholdMemberList(household)
            .then((reportId) => {
              reportIds.push(reportId);
              return reportIds;
            });
        });
      }, Promise.resolve([]));

      createSequentially.then((reportIds) => {
        return this.reportStore.downloadMultipleReportsZipped(reportIds, this.selectedHouseholds);
      }).then(() => this.finished = true)
        .catch((e) => {
          this.popupMessage = e?.message || "Something went wrong generating the report!";
          this.$refs.popup.show();
          this.reset();
        });
    },

    householdIsSelected(household) {
      return this.selectedHouseholds.find(selected => selected.id === household.id);
    },

    selectYear(year) {
      if (this.generating || this.finished) return;
      this.selectedYear = year;
    },

    loadMoreYears() {
      this.visibleYearCount += 5;
    },

    countUploadedReports() {
      let count = 0;
      this.selectedHouseholds.forEach(household => {
        if (household.pendingReportStatus === "COMPLETED") {
          count += 1;
        }
        if (household.pendingReportStatus === "PROCESSING") {
          count += 0.5;
        }
        if (household.pendingReportStatus === "CREATED") {
          count += 0.25;
        }
      });
      this.uploadedReportsCount = count;
    }
  }
};

</script>

<template>
  <div
    v-if="loading"
    class="min-h-60 flex justify-center content-center"
  >
    <loading-spinner size="small" />
  </div>
  <div
    v-else
    class="report-generator-container"
  >
    <div
      v-if="title || description"
      class="header"
    >
      <div class="headline-3-regular">
        {{ title }}
      </div>
      <div class="body-2-light">
        {{ description }}
      </div>
    </div>
    <div
      v-if="showYears"
      class="year-selector"
    >
      <div class="section-label">
        Select Year
      </div>
      <div class="flex flex-row gap-3 flex-wrap items-center">
        <div
          v-for="year in years"
          :key="year"
          :class="{selected: year === selectedYear}"
          class="year-chip"
          @click="selectYear(year)"
        >
          {{ year.toString() }}
        </div>
        <button
          class="show-more-btn text-xs px-3 py-1 rounded hover:bg-gray-100 transition-colors"
          @click="loadMoreYears"
        >
          Show More
        </button>
      </div>
    </div>


    <div class="content-container">
      <div class="flex flex-col flex-1">
        <!-- Search/ add all, clear -->
        <div class="controls-container">
          <div class="w-full flex flex-row justify-between mb-1">
            <div class="section-label">
              Select Households to Include
            </div>
            <div class="font-semibold text-gray-500">
              ({{ selectedHouseholds.length }} of {{ households.length }} selected)
            </div>
          </div>
          <div class="flex flex-row gap-2">
            <div class="search-input-wrapper flex-1">
              <icon
                icon="search"
                class="search-icon"
              />

              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by household name..."
                class="search-input"
              >
              <button
                v-if="searchQuery"
                class="clear-search-btn"
                @click="clearSearch"
              >
                <icon icon="x" />
              </button>
            </div>

            <button
              :disabled="filteredAvailableHouseholds.length === 0 || generating || finished"
              class="btn btn-primary btn-small"
              @click="addAllHouseholds"
            >
              Add All
            </button>

            <button
              :disabled="selectedHouseholds.length === 0 || generating || finished"
              class="btn btn-outline btn-secondary btn-small"
              @click="clearSelectedHouseholds"
            >
              <span>Clear All </span>
            </button>
          </div>
        </div>
        <!-- Household list -->

        <div class="donors-list clean-scrollbar">
          <div
            v-if="filteredAvailableHouseholds.length === 0 && searchQuery"
            class="empty-state"
          >
            <p class="empty-primary">
              No households found
            </p>
            <p class="empty-secondary">
              Try adjusting your search criteria
            </p>
          </div>

          <div
            v-else-if="filteredAvailableHouseholds.length === 0"
            class="empty-state"
          >
            <p class="empty-primary">
              All households are selected
            </p>
            <p class="empty-secondary">
              No more households available to add
            </p>
          </div>

          <div
            class="donor-list-container"
            :class="{generating: generating}"
          >
            <div
              v-for="household in visibleHouseholds"
              :key="`${household.id}`"
              class="donor-info-card"
              :class="[
                householdIsSelected(household) ? 'selected-donor' : 'unselected-donor',
                !generating || householdIsSelected(household) ? 'visible' :'collapsed']"
              @click="toggleHousehold(household)"
            >
              <div class="user-info">
                <div class="user-details">
                  <div class="user-name">
                    <span>{{ household.name }}</span>
                  </div>
                  <div class="user-email">
                    {{ household.member_count }} member{{ household.member_count !== 1 ? 's' : '' }}
                  </div>
                </div>
              </div>
              <div
                class="user-actions"
              >
                <button
                  v-if="!householdIsSelected(household) && !generating"
                  class="add-btn"
                  title="Add household"
                >
                  <icon
                    icon="plus"
                    size="16px"
                  />
                </button>
                <button
                  v-else-if="householdIsSelected(household) && !generating"
                  class="remove-btn"
                  title="Remove household"
                >
                  <icon
                    icon="check"
                    color="darkgreen"
                    class="default-icon"
                  />
                  <icon
                    icon="x"
                    color="red"
                    class="hover-icon"
                  />
                </button>
              </div>
            </div>
            <button
              v-if="hasMoreHouseholds"
              class="load-more-donors-btn"
              @click="paginatedHouseholdList += 50"
            >
              Show More ({{ filteredAvailableHouseholds.length - paginatedHouseholdList }} remaining)
            </button>
          </div>
        </div>
      </div>
      <div class="flex-1 flex flex-col items-center content-center mt-32">
        <div
          v-if="finished"
          class="flex flex-col w-full items-center gap-4 mb-12"
        >
          <div class="text-lg">
            Your Reports will download shortly ...
          </div>
          <div class="flex flex-row gap-2">
            <button
              class="btn btn-filled"
              @click="reset"
            >
              Another Report
            </button>
          </div>
        </div>

        <div v-else-if="generating">
          <loading-spinner
            size="small"
            :max="selectedHouseholds.length"
            :value="uploadedReportsCount"
            percentage
            prevent-full-percent
          />
        </div>

        <button
          v-else
          role="button"
          :disabled="!generateButtonEnabled"
          class="btn btn-filled flex flex-row"
          @click="generateReport"
        >
          <span>Download</span>
          <icon
            icon="cloud-arrow-down"
            size="24px"
          />
        </button>
      </div>
    </div>
  </div>
  <popup
    ref="popup"
    :message="popupMessage"
    :duration="3000"
  />
</template>

<style scoped lang="scss">
.report-generator-container {
  @apply w-full flex flex-col h-full max-h-full;

  @screen md {
    @apply p-4;
  }

  .year-chip {
    @apply py-0.5 px-2 rounded-md font-semibold cursor-pointer;
    @apply flex items-center content-center;
    @apply border-2;
    &:hover, &.selected {
      @apply bg-blue-500 text-white;
    }

    &.selected {
      @apply border-none;
    }
  }

  .content-container {
    @apply flex flex-row overflow-y-auto h-full;
    @apply border p-3 rounded-md bg-gray-50;
  }
}

.header {
  @apply pb-4 border-b-2 mb-4 border-gray-200;
}

.section-label {
  @apply font-semibold;
}

.divider {
  @apply w-px bg-gray-200 my-5;
}

.year-selector {
  @apply mb-4;
}

.controls-container {
  @apply mb-2;
  .search-input-wrapper {
    @apply relative flex items-center w-full;
  }

  .search-icon {
    @apply absolute left-3 text-gray-400 pointer-events-none;
  }

  .search-input {
    @apply w-full py-2 px-3 pl-9 pr-10 border border-gray-300 rounded-md text-sm outline-none;
    @apply transition-all duration-150 focus:border-blue-500 focus:ring-2 focus:ring-blue-100;
  }
}

.donors-list {
  @apply h-full flex flex-col gap-1 overflow-y-auto border rounded-md bg-white;
  @apply p-3 pb-16;
}

.empty-state {
  @apply text-center py-12;
}

.empty-primary {
  @apply text-gray-900 font-medium mb-1;
}

.empty-secondary {
  @apply text-gray-500 text-sm;
}

.donor-list-container {
  @apply flex flex-col gap-2;
  .donor-info-card {
    @apply flex items-center justify-between p-2 bg-gray-50;
    @apply border border-gray-200 rounded-lg cursor-pointer transition-all;

    &.collapsed {
      animation: collapseHeight .6s ease-in-out forwards;
    }

    @apply h-16;
    &:hover {
      @apply shadow-sm border-gray-300;
    }
  }

  .selected-donor {
    @apply bg-green-50 border-green-800;

    &:hover {
      @apply bg-green-100 border-green-800;
    }
  }

  .unselected-donor {
    &:hover {
      @apply bg-gray-50;
    }
  }

  .user-info {
    @apply flex items-center gap-3 flex-1 min-w-0;
  }

  .user-details {
    @apply min-w-0 flex-1;
  }

  .user-name {
    @apply font-medium text-gray-900 flex gap-1;
  }

  .user-email {
    @apply text-sm text-gray-500 truncate;
  }
}

.btn.btn-filled {
  :deep(svg) path {
    fill: white;
  }
}

.remove-btn .hover-icon {
  display: none;
}

.remove-btn .default-icon {
  display: inline;
}

.remove-btn:hover .hover-icon {
  display: inline;
}

.remove-btn:hover .default-icon {
  display: none;
}
.load-more-donors-btn {
  @apply w-full py-2 mt-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors;
}

@keyframes collapseHeight {
  0% {
    opacity: 1;
  }
  40% {
    @apply h-8 m-0 opacity-60;
  }
  60% {
    @apply h-4 opacity-20;
  }
  80% {
    @apply h-0 opacity-0;
  }
  100% {
    @apply h-0 opacity-0;
    display: none;
  }
}
</style>
